import * as React from 'react';
import { format, isFirstDayOfMonth, startOfDay } from 'date-fns';
import { AxisBottom, AxisLeft } from '@visx/axis';
import { localPoint } from '@visx/event';
import { GridRows } from '@visx/grid';
import { Group } from '@visx/group';
import { scaleLinear, scaleTime } from '@visx/scale';
import { Bar, Line, LinePath } from '@visx/shape';
import { TooltipWithBounds, useTooltip } from '@visx/tooltip';
import { DisplayStep } from './analyticsWindow';
import { SeriesPoint } from './useAnalyticsSeries';
import styles from './Analytics.module.css';

const MARGIN = { top: 8, right: 8, bottom: 28 };
// Roughly the width of one 11px axis-label character, to size the left margin to the labels.
const LABEL_CHAR_WIDTH = 6.5;
const AXIS_COLOR = '#9f9eb5';
const GRID_COLOR = '#eef1f7';
const SURFACE = '#fff';
const SELECTION_FILL = 'rgba(66, 134, 243, 0.12)';
// Shorter than this is a click, not a selection.
const MIN_DRAG_PX = 4;

const STEP_FORMAT: Record<DisplayStep, string> = {
  '5m': 'dd/MM HH:mm',
  '1h': 'dd/MM HH:mm',
  '1d': 'dd/MM/yyyy',
  '1mo': 'MM/yyyy',
};

const HOUR_MS = 3600 * 1000;

/** 24-hour, day-first labels like the rest of the dashboard; d3's default would show "02 PM". */
function timeTickFormat(span: number) {
  return (value: Date | { valueOf(): number }) => {
    const time = value.valueOf();
    const isMidnight = startOfDay(time).getTime() === time;
    if (span <= 3 * 24 * HOUR_MS) return isMidnight ? format(time, 'dd/MM') : format(time, 'HH:mm');
    // Ticks on month starts mark months once the view spans a couple of them.
    if (span > 60 * 24 * HOUR_MS && isMidnight && isFirstDayOfMonth(time)) return format(time, 'MM/yyyy');
    return format(time, 'dd/MM');
  };
}

export type ChartSeries = { id: string; label: string; color: string; points: SeriesPoint[] };

type TimeSeriesChartProps = {
  width: number;
  height: number;
  /** Visible series only; their colors are fixed by the caller so hiding one never repaints another. */
  series: ChartSeries[];
  chart: 'bar' | 'line';
  start: number;
  end: number;
  step: DisplayStep;
  formatValue: (value: number) => string;
  /** Spacing between value ticks for a given maximum, e.g. round durations. Defaults to a nice linear scale. */
  tickStep?: (max: number) => number;
  /** Called with the time range dragged across the plot. */
  onSelectRange?: (start: number, end: number) => void;
};

type Hover = { start: number; end: number };

const TimeSeriesChart: React.FC<TimeSeriesChartProps> = ({
  width,
  height,
  series,
  chart,
  start,
  end,
  step,
  formatValue,
  tickStep,
  onSelectRange,
}) => {
  const { tooltipData, tooltipLeft, tooltipTop, showTooltip, hideTooltip } = useTooltip<Hover>();
  const innerHeight = Math.max(0, height - MARGIN.top - MARGIN.bottom);

  // Every step with data in any visible series, with the value per series; bars stack in series order.
  const steps = React.useMemo(() => {
    const byStart = new Map<number, { start: number; end: number; values: Map<string, number> }>();
    for (const item of series) {
      for (const point of item.points) {
        const entry = byStart.get(point.start) ?? { start: point.start, end: point.end, values: new Map() };
        entry.values.set(item.id, point.value);
        byStart.set(point.start, entry);
      }
    }
    return Array.from(byStart.values()).sort((a, b) => a.start - b.start);
  }, [series]);

  const maxValue = Math.max(
    1,
    ...steps.map((entry) =>
      chart === 'bar'
        ? Array.from(entry.values.values()).reduce((sum, value) => sum + value, 0)
        : Math.max(...entry.values.values())
    )
  );

  const valueStep = tickStep?.(maxValue);
  const yMax = valueStep ? Math.ceil(maxValue / valueStep) * valueStep : maxValue;
  const yTicks = React.useMemo(() => {
    if (valueStep) return Array.from({ length: Math.round(yMax / valueStep) + 1 }, (_, index) => index * valueStep);
    return scaleLinear({ domain: [0, yMax], nice: true }).ticks(4);
  }, [valueStep, yMax]);
  const left = Math.max(32, ...yTicks.map((tick) => formatValue(tick).length * LABEL_CHAR_WIDTH + 12));
  const innerWidth = Math.max(0, width - left - MARGIN.right);

  const xScale = React.useMemo(
    () => scaleTime({ domain: [new Date(start), new Date(end)], range: [0, innerWidth] }),
    [start, end, innerWidth]
  );
  const yScale = React.useMemo(
    () => scaleLinear({ domain: [0, Math.max(yMax, yTicks[yTicks.length - 1])], range: [innerHeight, 0] }),
    [yMax, yTicks, innerHeight]
  );
  const xOf = (time: number) => Math.min(innerWidth, Math.max(0, xScale(new Date(time))));
  const midOf = (entry: Hover) => (xOf(entry.start) + xOf(entry.end)) / 2;

  const [drag, setDrag] = React.useState<{ from: number; to: number } | null>(null);
  const dragRef = React.useRef(drag);
  dragRef.current = drag;
  const dragging = drag !== null;

  // Released anywhere, also outside the graph, so a drag never gets stuck.
  React.useEffect(() => {
    if (!dragging) return;
    const finish = () => {
      const current = dragRef.current;
      setDrag(null);
      if (!current || Math.abs(current.to - current.from) < MIN_DRAG_PX) return;
      const from = Math.min(current.from, current.to);
      const to = Math.max(current.from, current.to);
      onSelectRange?.(xScale.invert(from).getTime(), xScale.invert(to).getTime());
    };
    window.addEventListener('mouseup', finish);
    return () => window.removeEventListener('mouseup', finish);
  }, [dragging, xScale, onSelectRange]);

  const plotX = (event: React.MouseEvent<SVGRectElement>) => {
    const coords = localPoint(event);
    return coords ? Math.min(innerWidth, Math.max(0, coords.x - left)) : null;
  };

  const handleDown = (event: React.MouseEvent<SVGRectElement>) => {
    const x = plotX(event);
    if (x === null || !onSelectRange) return;
    event.preventDefault();
    hideTooltip();
    setDrag({ from: x, to: x });
  };

  const handleMove = (event: React.MouseEvent<SVGRectElement>) => {
    if (drag) {
      const x = plotX(event);
      if (x !== null) setDrag({ ...drag, to: x });
      return;
    }
    const coords = localPoint(event);
    if (!coords || steps.length === 0) return;
    const x = coords.x - left;
    const nearest = steps.reduce((best, entry) =>
      Math.abs(midOf(entry) - x) < Math.abs(midOf(best) - x) ? entry : best
    );
    showTooltip({
      tooltipData: { start: nearest.start, end: nearest.end },
      tooltipLeft: coords.x,
      tooltipTop: coords.y,
    });
  };

  const hovered = tooltipData && steps.find((entry) => entry.start === tooltipData.start);

  return (
    <div style={{ position: 'relative' }}>
      <svg width={width} height={height}>
        <Group left={left} top={MARGIN.top}>
          <GridRows scale={yScale} width={innerWidth} stroke={GRID_COLOR} tickValues={yTicks} />
          {chart === 'bar' &&
            steps.map((entry) => {
              const x = xOf(entry.start);
              const right = xOf(entry.end);
              // A 1px surface gap between neighbouring bars once there is room for it.
              const barWidth = Math.max(1, right - x - (right - x > 4 ? 1 : 0));
              let base = innerHeight;
              return series.map((item) => {
                const value = entry.values.get(item.id);
                if (!value) return null;
                const top = yScale(yScale.invert(base) + value);
                const bar = (
                  <Bar
                    key={`${item.id}-${entry.start}`}
                    x={x}
                    y={top}
                    width={barWidth}
                    height={Math.max(0, base - top)}
                    fill={item.color}
                    opacity={hovered && hovered.start !== entry.start ? 0.6 : 1}
                  />
                );
                base = top;
                return bar;
              });
            })}
          {chart === 'line' && hovered && (
            <Line
              from={{ x: midOf(hovered), y: 0 }}
              to={{ x: midOf(hovered), y: innerHeight }}
              stroke={AXIS_COLOR}
              strokeDasharray="3 3"
              pointerEvents="none"
            />
          )}
          {chart === 'line' &&
            series.map((item) => (
              <g key={item.id}>
                <LinePath
                  data={item.points}
                  x={(point) => midOf(point)}
                  y={(point) => yScale(point.value)}
                  stroke={item.color}
                  strokeWidth={2}
                />
                {item.points.map((point) => (
                  <circle
                    key={point.start}
                    cx={midOf(point)}
                    cy={yScale(point.value)}
                    r={hovered?.start === point.start ? 5 : 4}
                    fill={item.color}
                    stroke={SURFACE}
                    strokeWidth={2}
                  />
                ))}
              </g>
            ))}
          <AxisLeft
            scale={yScale}
            tickValues={yTicks}
            tickFormat={(value) => formatValue(Number(value))}
            stroke={AXIS_COLOR}
            tickStroke={AXIS_COLOR}
            hideAxisLine
            tickLabelProps={{ fill: AXIS_COLOR, fontSize: 11, textAnchor: 'end', dx: -4, dy: 3 }}
          />
          <AxisBottom
            top={innerHeight}
            scale={xScale}
            tickFormat={timeTickFormat(end - start)}
            numTicks={Math.max(2, Math.floor(innerWidth / 90))}
            stroke={AXIS_COLOR}
            tickStroke={AXIS_COLOR}
            tickLabelProps={{ fill: AXIS_COLOR, fontSize: 11, textAnchor: 'middle' }}
          />
          {drag && (
            <rect
              x={Math.min(drag.from, drag.to)}
              width={Math.abs(drag.to - drag.from)}
              height={innerHeight}
              fill={SELECTION_FILL}
              stroke="var(--cornflower-blue)"
              strokeWidth={1}
              pointerEvents="none"
            />
          )}
          <rect
            width={innerWidth}
            height={innerHeight}
            fill="transparent"
            style={{ cursor: onSelectRange ? 'crosshair' : undefined }}
            onMouseDown={handleDown}
            onMouseMove={handleMove}
            onMouseLeave={hideTooltip}
          />
        </Group>
      </svg>
      {hovered && (
        <TooltipWithBounds left={tooltipLeft} top={tooltipTop} className={styles.Tooltip}>
          <div>{format(hovered.start, STEP_FORMAT[step])}</div>
          {series.map((item) => {
            const value = hovered.values.get(item.id);
            return (
              <div key={item.id} className={styles.TooltipRow}>
                <span className={styles.Swatch} style={{ background: item.color }} />
                {item.label}:{' '}
                <span className={styles.TooltipValue}>{value === undefined ? '–' : formatValue(value)}</span>
              </div>
            );
          })}
        </TooltipWithBounds>
      )}
    </div>
  );
};

export default TimeSeriesChart;
